// src/constants/PrinterConstants.js
// *******************************************************************************
// PRINTER CONSTANTS
// *******************************************************************************
// Configuration constants for thermal printers and receipt printing.
// These values will be moved to database for user configuration in the future.
// *******************************************************************************

// *******************************************************************************
// PRINTER CONFIGURATION
// *******************************************************************************

/**
 * Default printer configuration
 * TODO: Move to database for user configuration
 *
 * @property {string} model - Printer model name
 * @property {string} connectionType - Connection type: 'usb' | 'serial' | 'network'
 * @property {string} printMode - Print mode: 'direct' | 'preview'
 * @property {number} paperWidth - Paper width in mm (58, 80)
 * @property {number} vendorId - USB Vendor ID (Epson = 0x04B8)
 * @property {number} productId - USB Product ID (T20X = 0x0202)
 * @property {Object} serialConfig - Serial port configuration (if connectionType is 'serial')
 * @property {Object} networkConfig - Network configuration (if connectionType is 'network')
 */
export const PRINTER_CONFIG = {
  // Printer identification
  model: 'Bematech MP-4200 TH',
  connectionType: 'usb', // 'usb' | 'serial' | 'network'
  printMode: 'direct', // 'direct' | 'preview'

  // Paper settings
  paperWidth: 80, // mm (58 or 80)
  charsPerLine: 48, // Characters per line for 80mm paper

  // USB Configuration (Bematech MP-4200 TH)
  usb: {
    vendorId: 0x0b1b, // Bematech vendor ID
    productId: 0x0003, // MP-4200 TH product ID
    // Alternative product IDs for Bematech thermal printers
    alternativeProductIds: [0x0003, 0x0001, 0x0002],
  },

  // Serial Configuration (if using serial connection)
  // Bematech MP-4200 TH typically uses 115200 baud
  serial: {
    baudRate: 115200,
    dataBits: 8,
    stopBits: 1,
    parity: 'none',
    flowControl: 'none',
  },

  // Network Configuration (if using network connection)
  network: {
    host: '192.168.1.100',
    port: 9100,
  },

  // Print settings
  settings: {
    cutPaper: true, // Auto cut after print
    openDrawer: false, // Open cash drawer before print
    beep: false, // Beep after print
    feedLines: 3, // Lines to feed before cut
  },
};

// *******************************************************************************
// ESC/POS COMMANDS
// *******************************************************************************

/**
 * ESC/POS command constants for thermal printers
 * Reference: https://reference.epson-biz.com/modules/ref_escpos/index.php
 */
export const ESC_POS = {
  // Printer initialization
  CANCEL: [0x18], // CAN - Cancel (clear print buffer)
  INIT: [0x1b, 0x40], // ESC @ - Initialize printer

  // Text formatting
  TXT_NORMAL: [0x1b, 0x21, 0x00], // ESC ! 0 - Normal text
  TXT_BOLD_ON: [0x1b, 0x45, 0x01], // ESC E 1 - Bold on
  TXT_BOLD_OFF: [0x1b, 0x45, 0x00], // ESC E 0 - Bold off
  TXT_UNDERLINE_ON: [0x1b, 0x2d, 0x01], // ESC - 1 - Underline on
  TXT_UNDERLINE_OFF: [0x1b, 0x2d, 0x00], // ESC - 0 - Underline off
  TXT_DOUBLE_HEIGHT: [0x1b, 0x21, 0x10], // ESC ! 16 - Double height
  TXT_DOUBLE_WIDTH: [0x1b, 0x21, 0x20], // ESC ! 32 - Double width
  TXT_DOUBLE_SIZE: [0x1b, 0x21, 0x30], // ESC ! 48 - Double height + width
  TXT_FONT_A: [0x1b, 0x4d, 0x00], // ESC M 0 - Font A (normal, 12x24)
  TXT_FONT_B: [0x1b, 0x4d, 0x01], // ESC M 1 - Font B (condensed, 9x17)
  TXT_CONDENSED_ON: [0x1b, 0x21, 0x01], // ESC ! 1 - Condensed mode on
  TXT_CONDENSED_OFF: [0x1b, 0x21, 0x00], // ESC ! 0 - Condensed mode off
  TXT_SIZE_NORMAL: [0x1d, 0x21, 0x00], // GS ! 0 - Normal size (1x width, 1x height)
  TXT_SIZE_SMALL: [0x1d, 0x21, 0x00, 0x1b, 0x4d, 0x01], // GS ! 0 + ESC M 1 - Small font

  // Text alignment
  ALIGN_LEFT: [0x1b, 0x61, 0x00], // ESC a 0 - Left align
  ALIGN_CENTER: [0x1b, 0x61, 0x01], // ESC a 1 - Center align
  ALIGN_RIGHT: [0x1b, 0x61, 0x02], // ESC a 2 - Right align

  // Line spacing
  LINE_SPACING_DEFAULT: [0x1b, 0x32], // ESC 2 - Default line spacing
  LINE_SPACING_SET: [0x1b, 0x33], // ESC 3 n - Set line spacing to n dots

  // Paper control
  FEED_LINE: [0x0a], // LF - Line feed
  FEED_LINES: (n) => [0x1b, 0x64, n], // ESC d n - Feed n lines
  CUT_PAPER: [0x1d, 0x56, 0x00], // GS V 0 - Full cut
  CUT_PAPER_PARTIAL: [0x1d, 0x56, 0x01], // GS V 1 - Partial cut
  CUT_PAPER_FEED: [0x1d, 0x56, 0x42, 0x00], // GS V B 0 - Feed and full cut

  // Cash drawer
  DRAWER_OPEN_PIN2: [0x1b, 0x70, 0x00, 0x19, 0xfa], // ESC p 0 - Open drawer (pin 2)
  DRAWER_OPEN_PIN5: [0x1b, 0x70, 0x01, 0x19, 0xfa], // ESC p 1 - Open drawer (pin 5)

  // Beep
  BEEP: [0x1b, 0x42, 0x05, 0x09], // ESC B 5 9 - Beep 5 times

  // Character set
  CHARSET_PC850: [0x1b, 0x74, 0x02], // ESC t 2 - PC850 (Multilingual Latin I)
  CHARSET_PC858: [0x1b, 0x74, 0x13], // ESC t 19 - PC858 (Euro)
  CHARSET_WPC1252: [0x1b, 0x74, 0x10], // ESC t 16 - WPC1252 (Latin I)

  // Code page for Brazilian Portuguese
  CODEPAGE_BRAZIL: [0x1b, 0x74, 0x03], // ESC t 3 - PC860 (Portuguese)

  // QR Code (GS ( k)
  QRCODE_MODEL: [0x1d, 0x28, 0x6b, 0x04, 0x00, 0x31, 0x41, 0x32, 0x00], // Model 2
  QRCODE_SIZE: (n) => [0x1d, 0x28, 0x6b, 0x03, 0x00, 0x31, 0x43, n], // Size (1-16)
  QRCODE_ERROR: [0x1d, 0x28, 0x6b, 0x03, 0x00, 0x31, 0x45, 0x31], // Error correction L
  QRCODE_STORE: (data) => {
    const len = data.length + 3;
    return [0x1d, 0x28, 0x6b, len & 0xff, (len >> 8) & 0xff, 0x31, 0x50, 0x30, ...data];
  },
  QRCODE_PRINT: [0x1d, 0x28, 0x6b, 0x03, 0x00, 0x31, 0x51, 0x30], // Print QR code

  // Barcode
  BARCODE_HEIGHT: (n) => [0x1d, 0x68, n], // GS h n - Set barcode height
  BARCODE_WIDTH: (n) => [0x1d, 0x77, n], // GS w n - Set barcode width (2-6)
  BARCODE_TEXT_BELOW: [0x1d, 0x48, 0x02], // GS H 2 - Print text below barcode
  BARCODE_CODE128: [0x1d, 0x6b, 0x49], // GS k 73 - Code128

  // Horizontal line (using special characters)
  LINE_SINGLE: '─'.repeat(48),
  LINE_DOUBLE: '═'.repeat(48),
  LINE_DASHED: '- '.repeat(24),
};

// *******************************************************************************
// PRINT TEMPLATES
// *******************************************************************************

/**
 * Print template settings
 */
export const PRINT_TEMPLATES = {
  danfeNfce: {
    showLogo: false, // Show company logo (requires image printing)
    showQrCode: true, // Show QR code
    showAccessKey: true, // Show access key
    showConsumer: true, // Show consumer data
    compactMode: false, // Compact item display
  },
};

// *******************************************************************************
// SUPPORTED PRINTERS
// *******************************************************************************

/**
 * List of supported thermal printers with their USB IDs
 * TODO: Move to database for dynamic configuration
 */
export const SUPPORTED_PRINTERS = [
  {
    brand: 'Epson',
    model: 'TM-T20',
    vendorId: 0x04b8,
    productIds: [0x0e03],
  },
  {
    brand: 'Epson',
    model: 'TM-T20II',
    vendorId: 0x04b8,
    productIds: [0x0e15],
  },
  {
    brand: 'Epson',
    model: 'TM-T20X',
    vendorId: 0x04b8,
    productIds: [0x0202, 0x0e28],
  },
  {
    brand: 'Epson',
    model: 'TM-T88V',
    vendorId: 0x04b8,
    productIds: [0x0202],
  },
  {
    brand: 'Elgin',
    model: 'i9',
    vendorId: 0x0dd4,
    productIds: [0x0200],
  },
  {
    brand: 'Bematech',
    model: 'MP-4200 TH',
    vendorId: 0x0b1b,
    productIds: [0x0003],
  },
];

// *******************************************************************************
// CP850 CHARACTER ENCODING
// *******************************************************************************

/**
 * CP850 (Code Page 850) character mapping for thermal printers
 * Maps Unicode characters to their CP850 byte values
 * Reference: https://en.wikipedia.org/wiki/Code_page_850
 */
const CP850_MAP = {
  // Special whitespace characters → regular space
  '\u00A0': 0x20, // Non-breaking space → space
  '\u2002': 0x20, // En space → space
  '\u2003': 0x20, // Em space → space
  '\u2009': 0x20, // Thin space → space

  // Currency and symbols
  '€': 0xd5, // Euro sign (CP858) - fallback to 'E' if not supported
  '¢': 0xbd,
  '£': 0x9c,
  '¥': 0xbe,
  '¤': 0xcf,
  '§': 0x15,
  '¶': 0x14,
  '©': 0x63, // Fallback to 'c'
  '®': 0x52, // Fallback to 'R'
  '™': 0x74, // Fallback to 't'

  // Portuguese/Brazilian accented characters (lowercase)
  'á': 0xa0,
  'à': 0x85,
  'â': 0x83,
  'ã': 0xc6,
  'ä': 0x84,
  'é': 0x82,
  'è': 0x8a,
  'ê': 0x88,
  'ë': 0x89,
  'í': 0xa1,
  'ì': 0x8d,
  'î': 0x8c,
  'ï': 0x8b,
  'ó': 0xa2,
  'ò': 0x95,
  'ô': 0x93,
  'õ': 0xe4,
  'ö': 0x94,
  'ú': 0xa3,
  'ù': 0x97,
  'û': 0x96,
  'ü': 0x81,
  'ç': 0x87,
  'ñ': 0xa4,

  // Portuguese/Brazilian accented characters (uppercase)
  'Á': 0xb5,
  'À': 0xb7,
  'Â': 0xb6,
  'Ã': 0xc7,
  'Ä': 0x8e,
  'É': 0x90,
  'È': 0xd4,
  'Ê': 0xd2,
  'Ë': 0xd3,
  'Í': 0xd6,
  'Ì': 0xde,
  'Î': 0xd7,
  'Ï': 0xd8,
  'Ó': 0xe0,
  'Ò': 0xe3,
  'Ô': 0xe2,
  'Õ': 0xe5,
  'Ö': 0x99,
  'Ú': 0xe9,
  'Ù': 0xeb,
  'Û': 0xea,
  'Ü': 0x9a,
  'Ç': 0x80,
  'Ñ': 0xa5,

  // Other common characters
  'ª': 0xa6,
  'º': 0xa7,
  '¿': 0xa8,
  '¡': 0xad,
  '«': 0xae,
  '»': 0xaf,
  '°': 0xf8,
  '±': 0xf1,
  '×': 0x78, // Fallback to 'x'
  '÷': 0xf6,
  'ß': 0xe1,

  // Box drawing characters (fallback to ASCII)
  '─': 0xc4,
  '│': 0xb3,
  '┌': 0xda,
  '┐': 0xbf,
  '└': 0xc0,
  '┘': 0xd9,
  '├': 0xc3,
  '┤': 0xb4,
  '┬': 0xc2,
  '┴': 0xc1,
  '┼': 0xc5,
  '═': 0xcd,
  '║': 0xba,
  '╔': 0xc9,
  '╗': 0xbb,
  '╚': 0xc8,
  '╝': 0xbc,
};

/**
 * Encode a string to CP850 byte array for thermal printers
 * @param {string} text - Unicode text to encode
 * @returns {number[]} Array of CP850 byte values
 */
export function encodeCP850(text) {
  if (!text) return [];

  const bytes = [];

  for (const char of text) {
    const code = char.charCodeAt(0);

    // ASCII characters (0x00-0x7F) pass through directly
    if (code < 128) {
      bytes.push(code);
    }
    // Check if we have a mapping for this character
    else if (CP850_MAP[char] !== undefined) {
      bytes.push(CP850_MAP[char]);
    }
    // Fallback: try to normalize accented characters
    else {
      // Remove diacritics using Unicode normalization
      const normalized = char.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
      if (normalized.length === 1 && normalized.charCodeAt(0) < 128) {
        bytes.push(normalized.charCodeAt(0));
      } else {
        // Ultimate fallback: question mark
        bytes.push(0x3f);
      }
    }
  }

  return bytes;
}
